import React, { useState, useEffect, useCallback } from 'react';
import { usersApi, teamsApi, authExtrasApi, formatApiError } from '../services/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { PencilSimple, Trash, Warning, Users, Key, Plus, MicrosoftOutlookLogo, MagnifyingGlass, Check } from '@phosphor-icons/react';
import { Checkbox } from '../components/ui/checkbox';
import { Toaster, toast } from 'sonner';

const ROLES = [
  { value: 'super_admin', label: 'Super Admin', color: '#002FA7' },
  { value: 'manager', label: 'Manager', color: '#0A0A0A' },
  { value: 'team_leader', label: 'Team Leader', color: '#52525B' },
  { value: 'employee', label: 'Employee', color: '#E4E4E7' },
];

const RoleBadge = ({ role }) => {
  const roleInfo = ROLES.find((r) => r.value === role) || ROLES[3];
  return (
    <span
      className="inline-flex items-center px-2 py-0.5 text-xs font-bold uppercase tracking-wider"
      style={{
        backgroundColor: roleInfo.color === '#E4E4E7' ? roleInfo.color : `${roleInfo.color}`,
        color: roleInfo.color === '#E4E4E7' ? '#0A0A0A' : '#FFFFFF',
      }}
    >
      {roleInfo.label}
    </span>
  );
};

const UsersPage = () => {
  const [users, setUsers] = useState([]);
  const [teams, setTeams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [formData, setFormData] = useState({ name: '', email: '', role: 'employee', team_ids: [], is_remote: false });
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [filterRole, setFilterRole] = useState('all');
  const [isResetPwdOpen, setIsResetPwdOpen] = useState(false);
  const [resetPwdUser, setResetPwdUser] = useState(null);
  const [newPassword, setNewPassword] = useState('');
  const [resettingPwd, setResettingPwd] = useState(false);
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [createData, setCreateData] = useState({ email: '', password: '', name: '', role: 'employee', team_ids: [] });
  const [creating, setCreating] = useState(false);
  const [isImportOpen, setIsImportOpen] = useState(false);
  const [entraUsers, setEntraUsers] = useState([]);
  const [entraSearch, setEntraSearch] = useState('');
  const [entraLoading, setEntraLoading] = useState(false);
  const [selectedEntraUsers, setSelectedEntraUsers] = useState(new Set());
  const [selectedEntraData, setSelectedEntraData] = useState(new Map()); // entra_id -> user data
  const [importTeamId, setImportTeamId] = useState('');
  const [importRole, setImportRole] = useState('employee');
  const [importActive, setImportActive] = useState(false);
  const [importing, setImporting] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [usersRes, teamsRes] = await Promise.all([
        usersApi.getAll(),
        teamsApi.getAll(),
      ]);
      setUsers(usersRes.data);
      setTeams(teamsRes.data);
    } catch (error) {
      console.error('Failed to fetch users:', error);
      toast.error('Failed to load users');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const openEditDialog = (user) => {
    setEditingUser(user);
    setFormData({
      name: user.name,
      email: user.email,
      role: user.role,
      team_ids: user.team_ids || [],
      is_remote: user.is_remote || false,
    });
    setError('');
    setIsDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSubmitting(true);
    try {
      await usersApi.update(editingUser.id, {
        name: formData.name,
        role: formData.role,
        team_ids: formData.team_ids.length > 0 ? formData.team_ids : [],
        is_remote: formData.is_remote,
      });
      toast.success('Utente aggiornato');
      setIsDialogOpen(false);
      fetchData();
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (userId) => {
    if (!window.confirm('Sei sicuro di voler eliminare questo utente?')) return;
    try {
      await usersApi.delete(userId);
      toast.success('Utente eliminato');
      fetchData();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const handleResetPassword = async () => {
    if (!resetPwdUser || !newPassword || newPassword.length < 6) {
      toast.error('La password deve avere almeno 6 caratteri');
      return;
    }
    setResettingPwd(true);
    try {
      await authExtrasApi.resetPassword(resetPwdUser.id, newPassword);
      toast.success(`Password reimpostata per ${resetPwdUser.name}`);
      setIsResetPwdOpen(false);
      setNewPassword('');
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setResettingPwd(false); }
  };

  const handleToggleActive = async (userId, currentStatus) => {
    try {
      await usersApi.update(userId, { is_active: !currentStatus });
      toast.success(!currentStatus ? 'Utente attivato' : 'Utente disattivato');
      fetchData();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const handleCreateUser = async () => {
    if (!createData.email || !createData.password || !createData.name) return;
    setCreating(true);
    try {
      await usersApi.create(createData);
      toast.success('Utente creato');
      setIsCreateOpen(false);
      setCreateData({ email: '', password: '', name: '', role: 'employee', team_ids: [] });
      fetchData();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setCreating(false); }
  };

  const openEntraImport = async () => {
    setIsImportOpen(true);
    setEntraSearch('');
    setSelectedEntraUsers(new Set());
    setSelectedEntraData(new Map());
    setImportTeamId('');
    setImportRole('employee');
    setImportActive(false);
    fetchEntraUsers('');
  };

  const fetchEntraUsers = async (search) => {
    setEntraLoading(true);
    try {
      const { data } = await authExtrasApi.listEntraUsers(search || undefined);
      setEntraUsers(data);
    } catch (err) {
      const msg = formatApiError(err);
      if (msg.includes('403') || msg.includes('Forbidden') || msg.includes('ermesso')) {
        toast.error(msg, { duration: 10000 });
      } else {
        toast.error(msg);
      }
      setEntraUsers([]);
    } finally { setEntraLoading(false); }
  };

  const handleImportSelected = async () => {
    if (selectedEntraUsers.size === 0) return;
    setImporting(true);
    try {
      const toImport = [...selectedEntraData.values()]
        .filter(u => !u.already_imported)
        .map(u => ({
          entra_id: u.entra_id, name: u.name, email: u.email,
          job_title: u.job_title, department: u.department,
          role: importRole, team_id: importTeamId || null, is_active: importActive,
        }));
      const { data } = await authExtrasApi.importEntraUsers(toImport);
      toast.success(`${data.imported} utenti importati${data.skipped > 0 ? `, ${data.skipped} già presenti` : ''}`);
      setIsImportOpen(false);
      fetchData();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setImporting(false); }
  };

  const toggleEntraUser = (entraId) => {
    const s = new Set(selectedEntraUsers);
    const d = new Map(selectedEntraData);
    if (s.has(entraId)) {
      s.delete(entraId);
      d.delete(entraId);
    } else {
      s.add(entraId);
      const userData = entraUsers.find(u => u.entra_id === entraId);
      if (userData) d.set(entraId, userData);
    }
    setSelectedEntraUsers(s);
    setSelectedEntraData(d);
  };

  const getTeamName = (teamId) => teams.find((t) => t.id === teamId)?.name || '-';
  const getTeamNames = (teamIds) => {
    if (!teamIds || teamIds.length === 0) return '-';
    return teamIds.map(tid => teams.find(t => t.id === tid)?.name || tid).join(', ');
  };

  const filteredUsers = filterRole === 'all' ? users : users.filter((u) => u.role === filterRole);

  return (
    <div data-testid="users-page">
      <Toaster position="top-right" richColors />

      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-4 md:mb-6 gap-3">
        <div>
          <h1 className="font-heading text-2xl md:text-3xl font-bold tracking-tight text-[#0A0A0A]">Utenti</h1>
          <p className="text-[#52525B] mt-1">Gestisci account utente e ruoli</p>
        </div>

        <div className="flex items-center gap-3">
          <Select value={filterRole} onValueChange={setFilterRole}>
            <SelectTrigger className="w-48 rounded-none border-[#E4E4E7]" data-testid="role-filter">
              <SelectValue placeholder="Filtra per ruolo" />
            </SelectTrigger>
            <SelectContent className="rounded-none">
              <SelectItem value="all" className="rounded-none">Tutti i Ruoli</SelectItem>
              {ROLES.map((role) => (
                <SelectItem key={role.value} value={role.value} className="rounded-none">{role.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button onClick={() => setIsCreateOpen(true)}
            className="bg-[#0A0A0A] text-white px-5 py-2.5 font-medium hover:bg-[#333] rounded-none" data-testid="create-user-btn">
            <Plus size={18} weight="bold" className="mr-2" /> Nuovo Utente
          </Button>
          <Button onClick={openEntraImport} variant="outline"
            className="border-[#002FA7] text-[#002FA7] hover:bg-[#002FA7] hover:text-white px-5 py-2.5 font-medium rounded-none" data-testid="import-entra-btn">
            <MicrosoftOutlookLogo size={18} weight="bold" className="mr-2" /> Importa da Entra ID
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="w-8 h-8 border-2 border-[#0A0A0A] border-t-transparent rounded-full animate-spin"></div>
        </div>
      ) : filteredUsers.length === 0 ? (
        <div className="border border-[#E4E4E7] p-12 text-center">
          <Users size={48} className="mx-auto text-[#E4E4E7] mb-4" />
          <h3 className="font-heading font-bold text-lg mb-2">Nessun utente trovato</h3>
          <p className="text-[#52525B]">Nessun utente corrisponde al filtro selezionato.</p>
        </div>
      ) : (
        <div className="border border-[#E4E4E7] overflow-x-auto">
          <table className="w-full data-table min-w-[700px]">
            <thead>
              <tr className="border-b-2 border-[#0A0A0A] bg-white">
                <th className="py-4 px-4 text-left">Utente</th>
                <th className="py-4 px-4 text-left">Email</th>
                <th className="py-4 px-4 text-left">Ruolo</th>
                <th className="py-4 px-4 text-left">Team</th>
                <th className="py-4 px-4 text-left">Stato</th>
                <th className="py-4 px-4 text-left">Registrato</th>
                <th className="py-4 px-4 text-right">Azioni</th>
              </tr>
            </thead>
            <tbody>
              {filteredUsers.map((user) => (
                <tr
                  key={user.id}
                  className="border-b border-[#E4E4E7] hover:bg-[#F4F4F5] transition-colors"
                  data-testid={`user-row-${user.id}`}
                >
                  <td className="py-3 px-4">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 bg-[#0A0A0A] flex items-center justify-center text-white font-bold text-xs">
                        {user.name?.split(' ').map((n) => n[0]).join('').toUpperCase().slice(0, 2)}
                      </div>
                      <span className="font-medium">{user.name}</span>
                    </div>
                  </td>
                  <td className="py-3 px-4 text-sm text-[#52525B]">{user.email}</td>
                  <td className="py-3 px-4">
                    <RoleBadge role={user.role} />
                  </td>
                  <td className="py-3 px-4 text-sm">{getTeamNames(user.team_ids)}</td>
                  <td className="py-3 px-4">
                    {user.is_active === false ? (
                      <button onClick={() => handleToggleActive(user.id, false)}
                        className="inline-flex items-center px-2 py-0.5 text-xs font-bold uppercase tracking-wider border border-[#F59E0B] text-[#F59E0B] bg-[#F59E0B]/5 hover:bg-[#F59E0B] hover:text-white transition-colors cursor-pointer"
                        data-testid={`activate-user-${user.id}`}>Inattivo</button>
                    ) : (
                      <span className="inline-flex items-center px-2 py-0.5 text-xs font-bold uppercase tracking-wider border border-[#22C55E] text-[#22C55E] bg-[#22C55E]/5">Attivo</span>
                    )}
                  </td>
                  <td className="py-3 px-4 font-mono text-xs">
                    {new Date(user.created_at).toLocaleDateString()}
                  </td>
                  <td className="py-3 px-4 text-right">
                    <div className="flex items-center justify-end gap-2">
                      <button
                        onClick={() => { setResetPwdUser(user); setNewPassword(''); setIsResetPwdOpen(true); }}
                        className="w-8 h-8 flex items-center justify-center border border-[#E4E4E7] hover:border-[#F59E0B] hover:text-[#F59E0B] transition-colors"
                        data-testid={`reset-pwd-${user.id}`}
                        title="Reset Password"
                      >
                        <Key size={16} weight="bold" />
                      </button>
                      <button
                        onClick={() => openEditDialog(user)}
                        className="w-8 h-8 flex items-center justify-center border border-[#E4E4E7] hover:border-[#0A0A0A] transition-colors"
                        data-testid={`edit-user-${user.id}`}
                        title="Modifica"
                      >
                        <PencilSimple size={16} weight="bold" />
                      </button>
                      <button
                        onClick={() => handleDelete(user.id)}
                        className="w-8 h-8 flex items-center justify-center border border-[#E4E4E7] hover:border-[#FF2A00] hover:text-[#FF2A00] transition-colors"
                        data-testid={`delete-user-${user.id}`}
                        title="Elimina"
                      >
                        <Trash size={16} weight="bold" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Edit User Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="sm:max-w-md rounded-none border border-[#0A0A0A]">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl font-bold">Modifica Utente</DialogTitle>
          </DialogHeader>
          {error && (
            <div className="bg-[#FF2A00]/5 border border-[#FF2A00] p-3 flex items-start gap-2">
              <Warning className="text-[#FF2A00] flex-shrink-0 mt-0.5" size={16} weight="bold" />
              <p className="text-[#FF2A00] text-sm">{error}</p>
            </div>
          )}
          <form onSubmit={handleSubmit} className="space-y-4 mt-4">
            <div>
              <Label className="text-xs tracking-[0.2em] uppercase font-bold text-[#52525B] mb-2 block">Nome</Label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="rounded-none border-[#E4E4E7]"
                data-testid="user-name-input"
                required
              />
            </div>
            <div>
              <Label className="text-xs tracking-[0.2em] uppercase font-bold text-[#52525B] mb-2 block">Email</Label>
              <Input value={formData.email} disabled className="rounded-none border-[#E4E4E7] bg-[#FAFAFA]" />
              <p className="text-xs text-[#52525B] mt-1">L'email non può essere modificata</p>
            </div>
            <div>
              <Label className="text-xs tracking-[0.2em] uppercase font-bold text-[#52525B] mb-2 block">Ruolo</Label>
              <Select value={formData.role} onValueChange={(v) => setFormData({ ...formData, role: v })}>
                <SelectTrigger className="rounded-none border-[#E4E4E7]" data-testid="user-role-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="rounded-none">
                  {ROLES.map((role) => (
                    <SelectItem key={role.value} value={role.value} className="rounded-none">
                      {role.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs tracking-[0.2em] uppercase font-bold text-[#52525B] mb-2 block">Team ({formData.team_ids.length})</Label>
              <div className="border border-[#E4E4E7] max-h-36 overflow-y-auto">
                {teams.map((team) => (
                  <label key={team.id} className="flex items-center gap-2 px-3 py-1.5 hover:bg-[#FAFAFA] cursor-pointer">
                    <Checkbox checked={formData.team_ids.includes(team.id)}
                      onCheckedChange={(checked) => {
                        const next = checked ? [...formData.team_ids, team.id] : formData.team_ids.filter(t => t !== team.id);
                        setFormData({ ...formData, team_ids: next });
                      }} />
                    <span className="text-sm">{team.name}</span>
                  </label>
                ))}
                {teams.length === 0 && <p className="text-xs text-[#A1A1AA] px-3 py-2">Nessun team disponibile</p>}
              </div>
            </div>
            <div className="flex items-center justify-between p-4 bg-[#FAFAFA] border border-[#E4E4E7]">
              <div>
                <Label className="font-medium">Utente Remoto</Label>
                <p className="text-xs text-[#52525B]">Di default in Smart Working anziché in ufficio</p>
              </div>
              <Switch checked={formData.is_remote}
                onCheckedChange={(v) => setFormData({ ...formData, is_remote: v })}
                data-testid="user-remote-switch" />
            </div>
            <div className="flex gap-3 pt-4">
              <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)} className="flex-1 rounded-none border-[#0A0A0A]">
                Annulla
              </Button>
              <Button type="submit" disabled={submitting} className="flex-1 bg-[#002FA7] text-white hover:bg-[#00278A] rounded-none" data-testid="save-user-btn">
                {submitting ? 'Salvataggio...' : 'Salva Modifiche'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Reset Password Dialog */}
      <Dialog open={isResetPwdOpen} onOpenChange={setIsResetPwdOpen}>
        <DialogContent className="sm:max-w-md rounded-none border border-[#0A0A0A]">
          <DialogHeader><DialogTitle className="font-heading text-xl font-bold">Reset Password</DialogTitle></DialogHeader>
          {resetPwdUser && (
            <div className="space-y-4 mt-4">
              <div className="bg-[#FAFAFA] border border-[#E4E4E7] p-4">
                <p className="text-sm"><strong>{resetPwdUser.name}</strong></p>
                <p className="text-sm text-[#52525B]">{resetPwdUser.email}</p>
              </div>
              <div>
                <Label className="text-xs tracking-[0.2em] uppercase font-bold text-[#52525B] mb-2 block">Nuova Password</Label>
                <Input type="password" value={newPassword} onChange={e => setNewPassword(e.target.value)}
                  placeholder="Minimo 6 caratteri" className="rounded-none border-[#E4E4E7]" data-testid="new-password-input" />
              </div>
              <div className="flex gap-3 pt-2">
                <Button type="button" variant="outline" onClick={() => setIsResetPwdOpen(false)} className="flex-1 rounded-none border-[#0A0A0A]">Annulla</Button>
                <Button type="button" disabled={resettingPwd || newPassword.length < 6} onClick={handleResetPassword}
                  className="flex-1 bg-[#F59E0B] text-white hover:bg-[#D97706] rounded-none" data-testid="confirm-reset-pwd-btn">
                  {resettingPwd ? 'Salvataggio...' : 'Imposta Password'}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Create User Dialog */}
      <Dialog open={isCreateOpen} onOpenChange={setIsCreateOpen}>
        <DialogContent className="sm:max-w-lg rounded-none border border-[#0A0A0A]">
          <DialogHeader><DialogTitle className="font-heading text-xl font-bold">Nuovo Utente</DialogTitle></DialogHeader>
          <div className="space-y-4 mt-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-xs tracking-[0.2em] uppercase font-bold text-[#52525B] mb-2 block">Nome Completo *</Label>
                <Input value={createData.name} onChange={e => setCreateData({...createData, name: e.target.value})}
                  placeholder="Mario Rossi" className="rounded-none border-[#E4E4E7]" data-testid="create-user-name" required />
              </div>
              <div>
                <Label className="text-xs tracking-[0.2em] uppercase font-bold text-[#52525B] mb-2 block">Email *</Label>
                <Input type="email" value={createData.email} onChange={e => setCreateData({...createData, email: e.target.value})}
                  placeholder="email@azienda.com" className="rounded-none border-[#E4E4E7]" data-testid="create-user-email" required />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-xs tracking-[0.2em] uppercase font-bold text-[#52525B] mb-2 block">Password *</Label>
                <Input type="password" value={createData.password} onChange={e => setCreateData({...createData, password: e.target.value})}
                  placeholder="Min 6 caratteri" className="rounded-none border-[#E4E4E7]" data-testid="create-user-password" required />
              </div>
              <div>
                <Label className="text-xs tracking-[0.2em] uppercase font-bold text-[#52525B] mb-2 block">Ruolo</Label>
                <Select value={createData.role} onValueChange={v => setCreateData({...createData, role: v})}>
                  <SelectTrigger className="rounded-none border-[#E4E4E7]" data-testid="create-user-role"><SelectValue /></SelectTrigger>
                  <SelectContent className="rounded-none">
                    {ROLES.map(r => <SelectItem key={r.value} value={r.value} className="rounded-none">{r.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label className="text-xs tracking-[0.2em] uppercase font-bold text-[#52525B] mb-2 block">Team ({createData.team_ids.length})</Label>
              <div className="border border-[#E4E4E7] max-h-36 overflow-y-auto">
                {teams.map(t => (
                  <label key={t.id} className="flex items-center gap-2 px-3 py-1.5 hover:bg-[#FAFAFA] cursor-pointer">
                    <Checkbox checked={createData.team_ids.includes(t.id)}
                      onCheckedChange={(checked) => {
                        const next = checked ? [...createData.team_ids, t.id] : createData.team_ids.filter(x => x !== t.id);
                        setCreateData({...createData, team_ids: next});
                      }} />
                    <span className="text-sm">{t.name}</span>
                  </label>
                ))}
              </div>
            </div>
            <div className="flex gap-3 pt-2">
              <Button type="button" variant="outline" onClick={() => setIsCreateOpen(false)} className="flex-1 rounded-none border-[#0A0A0A]">Annulla</Button>
              <Button type="button" disabled={creating || !createData.email || !createData.name || createData.password.length < 6}
                onClick={handleCreateUser}
                className="flex-1 bg-[#0A0A0A] text-white hover:bg-[#333] rounded-none" data-testid="confirm-create-user-btn">
                {creating ? 'Creazione...' : 'Crea Utente'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Import Entra ID Users Dialog */}
      <Dialog open={isImportOpen} onOpenChange={setIsImportOpen}>
        <DialogContent className="sm:max-w-2xl rounded-none border border-[#002FA7] max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-heading text-xl font-bold flex items-center gap-2">
            <MicrosoftOutlookLogo size={24} weight="bold" className="text-[#002FA7]" /> Importa Utenti da Entra ID
          </DialogTitle></DialogHeader>
          <div className="space-y-4 mt-4">
            {/* Search */}
            <div className="flex gap-2">
              <div className="flex-1 relative">
                <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#A1A1AA]" />
                <Input value={entraSearch} onChange={e => setEntraSearch(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && fetchEntraUsers(entraSearch)}
                  placeholder="Cerca per nome o email..."
                  className="rounded-none border-[#E4E4E7] pl-9" data-testid="entra-search" />
              </div>
              <Button onClick={() => fetchEntraUsers(entraSearch)} variant="outline"
                className="rounded-none border-[#E4E4E7]" data-testid="entra-search-btn">Cerca</Button>
            </div>

            {/* Config: role + team + active */}
            <div className="grid grid-cols-3 gap-3">
              <div>
                <Label className="text-[10px] tracking-[0.2em] uppercase font-bold text-[#52525B] mb-1 block">Ruolo</Label>
                <Select value={importRole} onValueChange={setImportRole}>
                  <SelectTrigger className="rounded-none border-[#E4E4E7] text-xs" data-testid="import-role"><SelectValue /></SelectTrigger>
                  <SelectContent className="rounded-none">
                    {ROLES.map(r => <SelectItem key={r.value} value={r.value} className="rounded-none">{r.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-[10px] tracking-[0.2em] uppercase font-bold text-[#52525B] mb-1 block">Team</Label>
                <Select value={importTeamId || 'none'} onValueChange={v => setImportTeamId(v === 'none' ? '' : v)}>
                  <SelectTrigger className="rounded-none border-[#E4E4E7] text-xs" data-testid="import-team"><SelectValue /></SelectTrigger>
                  <SelectContent className="rounded-none">
                    <SelectItem value="none" className="rounded-none">Nessun Team</SelectItem>
                    {teams.map(t => <SelectItem key={t.id} value={t.id} className="rounded-none">{t.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex items-end pb-1">
                <label className="flex items-center gap-2 cursor-pointer">
                  <Checkbox checked={importActive} onCheckedChange={setImportActive} />
                  <span className="text-xs font-medium">Attivi subito</span>
                </label>
              </div>
            </div>

            {/* User list */}
            {entraLoading ? (
              <div className="flex justify-center py-8"><div className="w-6 h-6 border-2 border-[#002FA7] border-t-transparent rounded-full animate-spin"></div></div>
            ) : entraUsers.length === 0 ? (
              <div className="py-8 text-center text-sm text-[#A1A1AA]">
                {entraSearch ? 'Nessun utente trovato' : 'Carica la lista utenti da Entra ID'}
              </div>
            ) : (
              <div className="border border-[#E4E4E7] max-h-72 overflow-y-auto">
                <div className="sticky top-0 bg-white border-b border-[#E4E4E7] px-3 py-2 flex items-center justify-between">
                  <span className="text-xs font-bold text-[#52525B]">{entraUsers.length} utenti trovati</span>
                  <button onClick={() => {
                    const importable = entraUsers.filter(u => !u.already_imported);
                    if (selectedEntraUsers.size === importable.length && importable.every(u => selectedEntraUsers.has(u.entra_id))) {
                      setSelectedEntraUsers(new Set());
                      setSelectedEntraData(new Map());
                    } else {
                      const s = new Set(selectedEntraUsers);
                      const d = new Map(selectedEntraData);
                      importable.forEach(u => { s.add(u.entra_id); d.set(u.entra_id, u); });
                      setSelectedEntraUsers(s);
                      setSelectedEntraData(d);
                    }
                  }} className="text-[10px] font-bold text-[#002FA7] hover:underline" data-testid="select-all-entra">
                    {selectedEntraUsers.size > 0 ? 'Deseleziona tutti' : 'Seleziona tutti'}
                  </button>
                </div>
                {entraUsers.map(eu => (
                  <div key={eu.entra_id}
                    className={`flex items-center gap-3 px-3 py-2.5 border-b border-[#E4E4E7] last:border-b-0 ${eu.already_imported ? 'opacity-50' : 'hover:bg-[#FAFAFA] cursor-pointer'} ${selectedEntraUsers.has(eu.entra_id) ? 'bg-[#002FA7]/5' : ''}`}
                    onClick={() => !eu.already_imported && toggleEntraUser(eu.entra_id)} data-testid={`entra-user-${eu.entra_id}`}>
                    <Checkbox checked={selectedEntraUsers.has(eu.entra_id)} disabled={eu.already_imported}
                      onCheckedChange={() => !eu.already_imported && toggleEntraUser(eu.entra_id)} />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium truncate">{eu.name}</p>
                      <p className="text-[10px] text-[#52525B] truncate">{eu.email}</p>
                    </div>
                    <div className="flex-shrink-0 text-right">
                      {eu.already_imported ? (
                        <span className="text-[10px] font-bold text-[#22C55E] flex items-center gap-1"><Check size={12} weight="bold" /> Importato</span>
                      ) : (
                        <div>
                          {eu.job_title && <p className="text-[10px] text-[#A1A1AA]">{eu.job_title}</p>}
                          {eu.department && <p className="text-[10px] text-[#A1A1AA]">{eu.department}</p>}
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}

            <div className="flex gap-3 pt-2">
              <Button variant="outline" onClick={() => setIsImportOpen(false)} className="flex-1 rounded-none border-[#0A0A0A]">Annulla</Button>
              <Button disabled={importing || selectedEntraUsers.size === 0} onClick={handleImportSelected}
                className="flex-1 bg-[#002FA7] text-white hover:bg-[#00278A] rounded-none" data-testid="confirm-import-btn">
                {importing ? 'Importazione...' : `Importa ${selectedEntraUsers.size} utenti`}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default UsersPage;
